import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ChampBadge from "@/components/ChampBadge";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill } from "@/components/perf";
import PageBackdrop from "@/components/PageBackdrop";
import Avatar from "@/components/Avatar";
import ShareMenu from "@/components/ShareMenu";
import AdBanner from "@/components/AdBanner";
import { usePlayerCard } from "@/context/PlayerCardContext";
import MessagesPanel from "@/components/MessagesPanel";
import NotificationsPanel from "@/components/NotificationsPanel";
import PostComments from "@/components/PostComments";
import { toast } from "sonner";
import { Heart, Send, Award, TrendingUp, Zap, MapPin, GraduationCap, MessageCircle, Bell, ImagePlus, X, MessageSquare, Loader2, Trash2, ExternalLink } from "lucide-react";
import CoachSignupForm from "@/components/CoachSignupForm";
import RivalryBoard from "@/components/RivalryBoard";

const PB_LOGO = "/brand/pb-logo.png";
import SuggestedFollows from "@/components/SuggestedFollows";

const ICONS = { milestone: Award, ranking: TrendingUp, breakthrough: Zap };
const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (v) => (v && v.startsWith("/api/") ? MEDIA_BASE + v : v);
const CHUNK_SIZE = 1.5 * 1024 * 1024;

function PhotoCarousel({ images, pid, onOpen }) {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const onScroll = () => { const el = ref.current; if (el) setIdx(Math.round(el.scrollLeft / el.clientWidth)); };
  const goTo = (i) => ref.current?.scrollTo({ left: i * ref.current.clientWidth, behavior: "smooth" });
  return (
    <div className="mb-3" data-testid={`post-carousel-${pid}`}>
      <div className="relative">
        <div ref={ref} onScroll={onScroll} style={{ scrollbarWidth: "none" }}
          className="flex overflow-x-auto snap-x snap-mandatory bg-black/60 border border-pb-border/40">
          {images.map((src, i) => (
            <div key={i} className="w-full shrink-0 snap-center flex items-center justify-center">
              <img src={src} alt="" onClick={() => onOpen(src)} loading="lazy"
                className="w-full max-h-[70vh] sm:max-h-[32rem] object-contain cursor-zoom-in"
                data-testid={`post-carousel-img-${pid}-${i}`} />
            </div>
          ))}
        </div>
        <span className="absolute top-2 right-2 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded-full pointer-events-none" data-testid={`carousel-counter-${pid}`}>
          {idx + 1}/{images.length}
        </span>
        {idx > 0 && (
          <button data-testid={`carousel-prev-${pid}`} onClick={() => goTo(idx - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/90 transition-colors">
            ‹
          </button>
        )}
        {idx < images.length - 1 && (
          <button data-testid={`carousel-next-${pid}`} onClick={() => goTo(idx + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/90 transition-colors">
            ›
          </button>
        )}
      </div>
      <div className="flex justify-center gap-1.5 mt-2" data-testid={`carousel-dots-${pid}`}>
        {images.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} data-testid={`carousel-dot-${pid}-${i}`}
            className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-pb-cyan" : "bg-zinc-600 hover:bg-zinc-400"}`} />
        ))}
      </div>
    </div>
  );
}

export default function Community() {
  const { user: authUser } = useAuth();
  const [feed, setFeed] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const { openPlayerCard } = usePlayerCard();
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [counts, setCounts] = useState({ messages: 0, notifications: 0 });
  const [showMessages, setShowMessages] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [openComments, setOpenComments] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const [f, h] = await Promise.all([api.get("/community/feed"), api.get("/community/highlights")]);
    setFeed(f.data); setHighlights(h.data);
  };
  const loadCounts = useCallback(async () => {
    try { const { data } = await api.get("/inbox/counts"); setCounts(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, []);
  useAutoRefresh(() => { load(); loadCounts(); });
  useEffect(() => {
    loadCounts();
    const iv = setInterval(loadCounts, 15000);
    return () => clearInterval(iv);
  }, [loadCounts]);
  useEffect(() => {
    if (params.get("open") === "messages") {
      setShowMessages(true);
      params.delete("open"); setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line

  const pickMedia = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    for (const file of files) {
      if (file.size > 25 * 1024 * 1024) { toast.error(`${file.name} is too big — max 25MB`); continue; }
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      if (!isVideo && !isImage) { toast.error("Only images and videos are supported"); continue; }
      const url = URL.createObjectURL(file);
      if (isVideo) {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => {
          if (v.duration > 20.5) { URL.revokeObjectURL(url); toast.error("Videos must be 20 seconds or shorter"); return; }
          setMedia((m) => { m.forEach((x) => URL.revokeObjectURL(x.url)); return [{ file, url, kind: "video" }]; });
        };
        v.onerror = () => { URL.revokeObjectURL(url); toast.error("Couldn't read that video file"); };
        v.src = url;
      } else {
        setMedia((m) => {
          if (m.some((x) => x.kind === "video")) { m.forEach((x) => URL.revokeObjectURL(x.url)); m = []; }
          if (m.length >= 6) { URL.revokeObjectURL(url); toast.error("Max 6 photos per post"); return m; }
          return [...m, { file, url, kind: "image" }];
        });
      }
    }
  };

  const uploadMedia = async (file) => {
    const uploadId = `post_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    let res = null;
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", i);
      fd.append("total_chunks", total);
      fd.append("filename", file.name);
      fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
      res = (await api.post("/community/upload/chunk", fd)).data;
    }
    return res;
  };

  const post = async () => {
    if (!content.trim() && media.length === 0) return;
    setBusy(true);
    try {
      let video = null;
      const images = [];
      if (media.length) {
        setUploading(true);
        for (const m of media) {
          const up = await uploadMedia(m.file);
          if (up.kind === "video") video = up.url; else images.push(up.url);
        }
        setUploading(false);
      }
      const { data } = await api.post("/community/posts", { content, image: images[0] || null, images, video });
      setFeed((f) => [data, ...f]); setContent("");
      media.forEach((m) => URL.revokeObjectURL(m.url));
      setMedia([]);
      toast.success("Posted to the feed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't post — try again");
    } finally { setBusy(false); setUploading(false); }
  };

  const like = async (id) => {
    const { data } = await api.post(`/community/posts/${id}/like`);
    setFeed((f) => f.map((p) => (p.id === id ? { ...p, likes: data.likes } : p)));
  };

  const deletePost = async (p) => {
    if (!window.confirm("Delete this post and all its comments?")) return;
    try {
      await api.delete(`/community/posts/${p.id}`);
      setFeed((f) => f.filter((x) => x.id !== p.id));
      toast.success("Post deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete post"); }
  };

  return (
    <div>
      <PageBackdrop ck="backdrop.community" image="https://static.prod-images.emergentagent.com/jobs/ccb7baf7-4fe3-4351-b3c9-d8e5a28ac676/images/bcebc3092d166d50d50b374ab923240487ab1f09d54a8a1aa95972b63e03c501.png" />
      <div className="relative z-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader ck="page.community" kicker="Social Layer · Engagement Feed"
          title="Community"
          subtitle="Share breakthroughs, track milestones, and feed off the energy of the Bullpen. Highlights are auto-generated from your performance." />
        <AdBanner zone="community" />
        <div className="flex gap-3">
          <button data-testid="messages-btn" onClick={() => setShowMessages(true)}
            className="relative w-11 h-11 border border-pb-border bg-pb-surface flex items-center justify-center hover:border-pb-red transition-colors">
            <MessageCircle className="w-5 h-5 text-zinc-300" />
            {counts.messages > 0 && (
              <span data-testid="messages-badge" className="absolute -top-1.5 -right-1.5 bg-pb-red text-black text-[10px] font-black px-1.5 py-0.5 min-w-[18px] text-center">{counts.messages}</span>
            )}
          </button>
          <button data-testid="notifications-btn" onClick={() => setShowNotifs(true)}
            className="relative w-11 h-11 border border-pb-border bg-pb-surface flex items-center justify-center hover:border-pb-red transition-colors">
            <Bell className="w-5 h-5 text-zinc-300" />
            {counts.notifications > 0 && (
              <span data-testid="notifications-badge" className="absolute -top-1.5 -right-1.5 bg-pb-red text-black text-[10px] font-black px-1.5 py-0.5 min-w-[18px] text-center">{counts.notifications}</span>
            )}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6 min-w-0">
          <SuggestedFollows />
          <div className="bg-pb-surface border border-pb-border p-5">
            <textarea data-testid="post-input" value={content} onChange={(e) => setContent(e.target.value)}
              placeholder="Share a training win, match result, or breakthrough…" rows={3}
              className="w-full bg-pb-elevated border border-pb-border p-3 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red resize-none" />
            {media.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="media-preview">
                {media.map((m, i) => (
                  <div key={m.url} className="relative">
                    {m.kind === "video"
                      ? <video src={m.url} className="max-h-32" controls muted />
                      : <img src={m.url} alt="" className="h-24 w-24 object-cover border border-pb-border" />}
                    <button data-testid={`media-remove-btn-${i}`}
                      onClick={() => { URL.revokeObjectURL(m.url); setMedia(media.filter((_, x) => x !== i)); }}
                      className="absolute top-1 right-1 bg-black/80 text-white p-1 hover:bg-pb-red hover:text-black transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {media[0]?.kind === "image" && media.length < 6 && (
                  <button data-testid="media-add-more-btn" onClick={() => fileRef.current?.click()}
                    className="h-24 w-24 border border-dashed border-pb-border text-zinc-500 hover:border-pb-cyan hover:text-pb-cyan flex items-center justify-center transition-colors">
                    <ImagePlus className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center justify-between mt-3">
              <input ref={fileRef} type="file" multiple accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden" onChange={pickMedia} data-testid="media-file-input" />
              <button data-testid="media-add-btn" onClick={() => fileRef.current?.click()}
                className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-pb-cyan transition-colors">
                <ImagePlus className="w-4 h-4" /> Photos / Video <span className="text-zinc-700 normal-case tracking-normal">(up to 6 photos, video max 20s)</span>
              </button>
              <button data-testid="post-submit" onClick={post} disabled={busy || (!content.trim() && media.length === 0)}
                className="flex items-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest px-5 py-2 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {uploading ? "Uploading…" : "Post"}
              </button>
            </div>
          </div>

          {feed.map((p) => (
            <div key={p.id} data-testid={`post-${p.id}`} className="bg-pb-surface border border-pb-border p-5 animate-fade-up">
              <div className="flex items-center gap-3 mb-3">
                <button data-testid={`post-author-btn-${p.id}`} onClick={() => p.user_id && openPlayerCard(p.user_id)}
                  disabled={!p.user_id}
                  className={`flex items-center gap-3 text-left ${p.user_id ? "cursor-pointer group" : "cursor-default"}`}>
                  <Avatar src={p.author_avatar || (p.author === "Pickle Bullpen" ? PB_LOGO : null)} name={p.author} size={40} testId={`post-avatar-${p.id}`}
                    className={p.user_id ? "group-hover:border-pb-red transition-colors" : ""} />
                  <div>
                    <div className={`font-semibold text-sm ${p.user_id ? "group-hover:text-pb-red transition-colors" : ""}`}>{p.author}<ChampBadge userId={p.user_id} /></div>
                    {p.author_location && (
                      <div className="flex items-center gap-1 text-[11px] text-zinc-500" data-testid={`post-location-${p.id}`}>
                        <MapPin className="w-3 h-3 text-pb-cyan" /> {p.author_location}
                      </div>
                    )}
                    {p.type === "highlight" && <Pill color="#4ADE80">Highlight</Pill>}
                    {p.type === "tournament" && <Pill color="#F59E0B">Tournament</Pill>}
                  </div>
                </button>
                {(authUser?.role === "admin" || (p.user_id && p.user_id === authUser?.id)) && (
                  <button data-testid={`post-delete-${p.id}`} onClick={() => deletePost(p)} title="Delete post"
                    className="ml-auto w-8 h-8 flex items-center justify-center text-zinc-600 hover:text-red-400 border border-transparent hover:border-red-500/40 transition-colors shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {p.content && <p className="text-zinc-200 mb-3 break-words">{p.content}</p>}
              {(() => {
                const imgs = (p.images && p.images.length ? p.images : (p.image ? [p.image] : [])).map(mediaSrc);
                if (imgs.length === 0) return null;
                if (imgs.length === 1) return (
                  <div className="mb-3 bg-black/60 border border-pb-border/40 flex items-center justify-center" data-testid={`post-image-frame-${p.id}`}>
                    <img src={imgs[0]} alt="" onClick={() => setLightbox(imgs[0])}
                      className="w-full max-h-[70vh] sm:max-h-[32rem] object-contain cursor-zoom-in hover:opacity-90 transition-opacity"
                      title="Click to view full photo" data-testid={`post-image-${p.id}`} />
                  </div>
                );
                return <PhotoCarousel images={imgs} pid={p.id} onOpen={setLightbox} />;
              })()}
              {p.video && <video src={mediaSrc(p.video)} className="w-full max-h-96 mb-3" controls playsInline data-testid={`post-video-${p.id}`} />}
              <div className="flex items-center gap-5">
                <button data-testid={`like-${p.id}`} onClick={() => like(p.id)}
                  className="flex items-center gap-2 text-sm text-zinc-500 hover:text-pb-red transition-colors">
                  <Heart className="w-4 h-4" /> {p.likes}
                </button>
                <button data-testid={`comments-toggle-${p.id}`} onClick={() => setOpenComments((o) => ({ ...o, [p.id]: !o[p.id] }))}
                  className="flex items-center gap-2 text-sm text-zinc-500 hover:text-pb-cyan transition-colors">
                  <MessageSquare className="w-4 h-4" /> {p.comment_count || 0}
                </button>
                <ShareMenu url={`${MEDIA_BASE}/api/share/posts/${p.id}`} text={p.content || ""}
                  image={p.image ? mediaSrc(p.image) : null} testId={`share-${p.id}`} />
              </div>
              {openComments[p.id] && (
                <PostComments postId={p.id}
                  onCountChange={(n) => setFeed((f) => f.map((x) => (x.id === p.id ? { ...x, comment_count: n } : x)))} />
              )}
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <RivalryBoard />
          <h3 className="font-display font-bold text-lg uppercase">Your Auto-Highlights</h3>
          {highlights.map((h, i) => {
            const Icon = ICONS[h.type] || Award;
            return (
              <div key={i} className="bg-pb-surface border border-pb-border p-4 flex gap-3 items-start hover:border-pb-cyan transition-colors">
                <div className="w-9 h-9 bg-pb-cyan/10 border border-pb-cyan/30 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-pb-cyan" />
                </div>
                <div>
                  <div className="font-display font-bold uppercase text-sm">{h.title}</div>
                  <div className="text-xs text-zinc-400 mt-1">{h.detail}</div>
                </div>
              </div>
            );
          })}
          <div className="bg-gradient-to-br from-pb-red/10 to-transparent border border-pb-red/30 p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-pb-red mb-2">Tournaments</div>
            <p className="text-sm text-zinc-300">Moneyball events & verified competitive tracking coming to your division soon.</p>
          </div>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-6 items-start" data-testid="coach-with-us">
        <div className="bg-gradient-to-br from-pb-cyan/10 to-transparent border border-pb-cyan/30 p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-3">
            <GraduationCap className="w-6 h-6 text-pb-cyan" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-pb-cyan font-bold">Coach With Us</span>
          </div>
          <h3 className="font-display font-black uppercase text-3xl leading-none mb-3">Bring the Bullpen<br />to your facility</h3>
          <p className="text-sm text-zinc-400 leading-relaxed">Are you a coach or facility owner? Apply to join the Pickle Bullpen coaching system — run drill sessions at your own facility, get listed for player bookings, and plug into our performance ecosystem. Fill out the application and we'll reach out to get you started.</p>
        </div>
        <div className="bg-pb-surface border border-pb-border p-6">
          <CoachSignupForm />
        </div>
      </div>
      {lightbox && createPortal(
        <div data-testid="image-lightbox" className="fixed inset-0 z-[999] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}>
          <div className="absolute top-4 right-4 flex gap-2 z-10">
            <button data-testid="lightbox-open-tab" onClick={(e) => { e.stopPropagation(); window.open(lightbox, "_blank", "noopener"); }}
              title="Open in new window"
              className="flex items-center gap-2 bg-pb-surface border border-pb-border text-zinc-200 hover:border-pb-cyan hover:text-pb-cyan text-xs font-bold uppercase tracking-widest px-4 py-2.5 transition-colors">
              <ExternalLink className="w-4 h-4" /> New Window
            </button>
            <button data-testid="lightbox-close" onClick={() => setLightbox(null)} title="Close"
              className="w-10 h-10 flex items-center justify-center bg-pb-surface border border-pb-border text-zinc-200 hover:border-pb-red hover:text-pb-red transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <img src={lightbox} alt="" data-testid="lightbox-image"
            className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>,
        document.body
      )}
      <MessagesPanel open={showMessages} onClose={() => { setShowMessages(false); loadCounts(); }} onRead={loadCounts} />
      <NotificationsPanel open={showNotifs} onClose={() => setShowNotifs(false)} onRead={loadCounts}
        onOpenMessages={() => { setShowNotifs(false); setShowMessages(true); }} />
      </div>
    </div>
  );
}
